import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { skillsApi } from '../utils/api';
import GlassCard from '../components/GlassCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ImportSkillModal from '../components/ImportSkillModal';
import { 
  Plus, 
  Star, 
  GitFork, 
  Eye, 
  Edit, 
  Trash2, 
  MoreVertical,
  Sparkles,
  TrendingUp,
  Calendar,
  Lock,
  Check,
  Filter,
  Share2,
  Copy,
  Globe,
  Link2,
  FileEdit,
  LayoutGrid,
  Download,
  Upload,
  Github
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { CategoryModal } from '../components/CategoryModal';

const MySkillsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  const [copiedSkillId, setCopiedSkillId] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importMethod, setImportMethod] = useState(null);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [skillToEditCategory, setSkillToEditCategory] = useState(null);

  // Status badge configuration
  const statusConfig = {
    DRAFT: {
      label: 'Draft',
      icon: <FileEdit className="h-3 w-3" />,
      className: 'bg-gray-100 text-gray-600 dark:bg-dark-bg-tertiary dark:text-gray-400 border-gray-300'
    },
    PRIVATE: {
      label: 'Private',
      icon: <Lock className="h-3 w-3" />,
      className: 'bg-gray-100 text-gray-700 dark:bg-dark-bg-tertiary dark:text-gray-300 border-gray-300 dark:border-[#2e2e2d]'
    },
    UNLISTED: {
      label: 'Unlisted',
      icon: <Link2 className="h-3 w-3" />,
      className: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 border-orange-300'
    },
    PUBLIC: {
      label: 'Public',
      icon: <Globe className="h-3 w-3" />,
      className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-300'
    }
  };

  useEffect(() => {
    loadSkills();
  }, [sortBy]);

  const loadSkills = async () => {
    try {
      setLoading(true);
      const res = await skillsApi.list({ user_id: user.id, sort: sortBy });
      setSkills(res.data || []);
    } catch (error) {
      console.error('Failed to load skills', error);
      toast.error('Failed to load your skills');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (skill) => {
    setSkillToDelete(skill);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!skillToDelete) return;
    
    try {
      await skillsApi.delete(skillToDelete.id);
      toast.success('Skill deleted successfully');
      setDeleteModalOpen(false);
      setSkillToDelete(null);
      loadSkills();
    } catch (error) {
      console.error('Failed to delete skill', error);
      toast.error('Failed to delete skill');
    }
  };

  const handleUpdateVisibility = async (skillId, newVisibility) => {
    try {
      await skillsApi.updateVisibility(skillId, newVisibility);
      toast.success(`Visibility updated to ${newVisibility}`);
      loadSkills();
    } catch (error) {
      console.error('Failed to update visibility', error);
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    }
  };

  const handleSaveCategory = async (category) => {
    if (!skillToEditCategory) return;
    
    try {
      console.log('Updating skill', skillToEditCategory.id, 'with category:', category);
      const response = await skillsApi.update(skillToEditCategory.id, { category });
      console.log('Category update response:', response);
      console.log('Updated skill data:', response.data);
      
      // Update the skill in the local skills list
      setSkills(skills.map(s => 
        s.id === skillToEditCategory.id ? response.data : s
      ));
      
      toast.success(`Category updated to ${category}`);
      setCategoryModalOpen(false);
      setSkillToEditCategory(null);
    } catch (error) {
      console.error('Failed to update category', error);
      console.error('Error details:', error.response);
      toast.error('Failed to update category');
    }
  };

  // Share functionality
  const getSkillUrl = (skill) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/skills/${skill.slug || skill.id}`;
  };

  const handleCopyLink = async (skill) => {
    const url = getSkillUrl(skill);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSkillId(skill.id);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedSkillId(null), 2000);
    } catch (error) {
      console.error('Failed to copy link', error);
      toast.error('Failed to copy link');
    }
  };

  const handleShareTwitter = (skill) => {
    const url = getSkillUrl(skill);
    const text = `Check out my Claude Skill: ${skill.name}`;
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
  };

  const handleShareLinkedIn = (skill) => {
    const url = getSkillUrl(skill);
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=520');
  };

  const handleShareFacebook = (skill) => {
    const url = getSkillUrl(skill);
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=550,height=420');
  };

  const categoryLabels = {
    'writing-content': 'Writing & Content',
    'business-productivity': 'Business & Productivity',
    'marketing-sales': 'Marketing & Sales',
    'education-learning': 'Education & Learning',
    'creative-design': 'Creative & Design',
    'skills-workflows': 'Skills & Workflows',
    'agents-ai': 'Agents & AI',
    'developer-tools': 'Developer Tools',
    'data-analytics': 'Data & Analytics',
    'documents-files': 'Documents & Files',
    'research-knowledge': 'Research & Knowledge',
    'web-apps': 'Web & Apps',
    'security-compliance': 'Security & Compliance',
    'devops-infrastructure': 'DevOps & Infrastructure',
    'science-research': 'Science & Research',
    'custom-experimental': 'Custom & Experimental'
  };

  // Extract unique categories from user's actual skills
  const userCategories = React.useMemo(() => {
    const categorySet = new Set();
    skills.forEach(skill => {
      // Check both category and category_id fields
      const category = skill.category_id || skill.category;
      if (category) {
        // Handle both string categories and object categories
        const categoryValue = typeof category === 'object' ? category.name : category;
        categorySet.add(categoryValue);
      }
    });
    return Array.from(categorySet);
  }, [skills]);

  const filteredSkills = selectedCategory === 'all' 
    ? skills 
    : skills.filter(skill => {
        const category = skill.category_id || skill.category;
        const categoryValue = typeof category === 'object' ? category.name : category;
        return categoryValue === selectedCategory;
      });

  const totalStats = skills.reduce(
    (acc, skill) => ({
      stars: acc.stars + (skill.star_count || 0),
      downloads: acc.downloads + (skill.install_count || 0),
    }),
    { stars: 0, downloads: 0 }
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2">
            My Skills
          </h1>
          <p className="text-lg text-text-gray dark:text-dark-text-secondary">
            Manage and track your Claude Skills
          </p>
        </div>
        
        <div className="flex gap-3">
          {/* Import Button - Outline Orange */}
          <Button
            onClick={() => {
              setImportMethod('github');
              setImportModalOpen(true);
            }}
            variant="outline"
            className="border-2 border-orange-500 text-orange-500 font-semibold rounded-xl hover:bg-orange-50 dark:hover:bg-orange-950/20 transition-all px-4 py-2"
          >
            <Github className="mr-2 h-4 w-4" />
            Import
          </Button>
          
          {/* Upload Button - Outline Orange */}
          <Button
            onClick={() => {
              setImportMethod('upload');
              setImportModalOpen(true);
            }}
            variant="outline"
            className="border-2 border-orange-500 text-orange-500 font-semibold rounded-xl hover:bg-orange-50 dark:hover:bg-orange-950/20 transition-all px-4 py-2"
          >
            <Upload className="mr-2 h-4 w-4" />
            Upload
          </Button>
          
          {/* Create Skill Button - Gradient Orange (Primary) */}
          <Button 
            onClick={() => navigate('/builder')} 
            className="gradient-primary text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            <Plus className="mr-2 h-5 w-5" />
            Create Skill
          </Button>
        </div>
      </div>

      {/* Stats Overview - 3 Metrics Only */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-xl">
              <Sparkles className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1">
            {skills.length}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            Total Skills
          </div>
        </GlassCard>

        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-primary/20 to-primary-dark/20 rounded-xl">
              <Star className="h-6 w-6 text-primary" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1">
            {totalStats.stars}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            Total Stars
          </div>
        </GlassCard>

        <GlassCard hover className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="p-3 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-xl">
              <Download className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-text-dark dark:text-dark-text mb-1">
            {totalStats.downloads}
          </div>
          <div className="text-sm text-text-gray dark:text-dark-text-secondary font-medium">
            Total Downloads
          </div>
        </GlassCard>
      </div>

      {/* Filter Bar */}
      <GlassCard variant="strong" className="p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Sort By */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-text-gray dark:text-dark-text-secondary" />
              <span className="text-sm font-medium text-text-dark dark:text-dark-text">Sort by:</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant={sortBy === 'recent' ? 'default' : 'outline'}
                onClick={() => setSortBy('recent')}
                size="sm"
                className={`rounded-xl ${sortBy === 'recent' ? 'gradient-primary text-white' : ''}`}
              >
                Recent
              </Button>
              <Button
                variant={sortBy === 'popular' ? 'default' : 'outline'}
                onClick={() => setSortBy('popular')}
                size="sm"
                className={`rounded-xl ${sortBy === 'popular' ? 'gradient-primary text-white' : ''}`}
              >
                Popular
              </Button>
              <Button
                variant={sortBy === 'alphabetical' ? 'default' : 'outline'}
                onClick={() => setSortBy('alphabetical')}
                size="sm"
                className={`rounded-xl ${sortBy === 'alphabetical' ? 'gradient-primary text-white' : ''}`}
              >
                A-Z
              </Button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-text-gray dark:text-dark-text-secondary" />
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[280px] rounded-xl">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-dark-bg-secondary">
                <SelectItem value="all">All Categories ({skills.length})</SelectItem>
                {userCategories.map(catId => {
                  const count = skills.filter(s => {
                    const skillCategory = s.category_id || s.category;
                    const categoryValue = typeof skillCategory === 'object' ? skillCategory.name : skillCategory;
                    return categoryValue === catId;
                  }).length;
                  return (
                    <SelectItem key={catId} value={catId}>
                      {categoryLabels[catId] || catId} ({count})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </GlassCard>

      {/* Skills Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <GlassCard key={i} className="h-64 skeleton"></GlassCard>
          ))}
        </div>
      ) : skills.length === 0 ? (
        <GlassCard variant="strong" className="text-center py-16">
          <div className="inline-block p-6 bg-gradient-to-br from-primary/10 to-primary-dark/10 rounded-2xl mb-4">
            <Sparkles className="h-16 w-16 text-primary mx-auto" />
          </div>
          <h3 className="text-2xl font-bold text-text-dark dark:text-dark-text mb-2">
            No skills yet
          </h3>
          <p className="text-text-gray dark:text-dark-text-secondary mb-6 max-w-md mx-auto">
            Create your first Claude Skill and start building your portfolio
          </p>
          <Button 
            onClick={() => navigate('/builder')} 
            className="gradient-primary text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl"
          >
            <Plus className="mr-2 h-5 w-5" />
            Create Your First Skill
          </Button>
        </GlassCard>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSkills.map((skill) => (
            <GlassCard key={skill.id} hover className="p-6 relative group">
              {/* Actions Dropdown */}
              <div className="absolute top-4 right-4 z-10">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="glass-strong w-48">
                    <DropdownMenuItem onClick={() => navigate(`/skills/${skill.id}/edit`)} className="cursor-pointer">
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/skills/${skill.id}`)} className="cursor-pointer">
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem 
                      onClick={() => {
                        setSkillToEditCategory(skill);
                        setCategoryModalOpen(true);
                      }} 
                      className="cursor-pointer"
                    >
                      <LayoutGrid className="h-4 w-4 mr-2" />
                      Category
                    </DropdownMenuItem>
                    
                    {/* Visibility Submenu - only for published skills */}
                    {skill.visibility !== 'DRAFT' && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          <Lock className="h-4 w-4 mr-2" />
                          Visibility
                        </DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                          <DropdownMenuSubContent className="bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-[#2e2e2d] shadow-lg">
                            <DropdownMenuItem
                              onClick={() => handleUpdateVisibility(skill.id, 'PRIVATE')}
                              className={`cursor-pointer ${skill.visibility === 'PRIVATE' ? 'bg-accent' : ''}`}
                            >
                              {skill.visibility === 'PRIVATE' && <Check className="h-4 w-4 mr-2" />}
                              {skill.visibility !== 'PRIVATE' && <span className="w-6" />}
                              Private
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateVisibility(skill.id, 'UNLISTED')}
                              className={`cursor-pointer ${skill.visibility === 'UNLISTED' ? 'bg-accent' : ''}`}
                            >
                              {skill.visibility === 'UNLISTED' && <Check className="h-4 w-4 mr-2" />}
                              {skill.visibility !== 'UNLISTED' && <span className="w-6" />}
                              Unlisted
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateVisibility(skill.id, 'PUBLIC')}
                              className={`cursor-pointer ${skill.visibility === 'PUBLIC' ? 'bg-accent' : ''}`}
                            >
                              {skill.visibility === 'PUBLIC' && <Check className="h-4 w-4 mr-2" />}
                              {skill.visibility !== 'PUBLIC' && <span className="w-6" />}
                              Public
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                      </DropdownMenuSub>
                    )}
                    
                    {/* Share Submenu */}
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer">
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="bg-white dark:bg-dark-bg-primary border border-gray-200 dark:border-[#2e2e2d] shadow-lg">
                          <DropdownMenuItem
                            onClick={() => handleCopyLink(skill)}
                            className="cursor-pointer"
                          >
                            {copiedSkillId === skill.id ? (
                              <>
                                <Check className="h-4 w-4 mr-2 text-green-600" />
                                Link Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="h-4 w-4 mr-2" />
                                Copy Link
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleShareTwitter(skill)}
                            className="cursor-pointer"
                          >
                            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                            Share on X
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleShareLinkedIn(skill)}
                            className="cursor-pointer"
                          >
                            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                            Share on LinkedIn
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleShareFacebook(skill)}
                            className="cursor-pointer"
                          >
                            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            Share on Facebook
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => openDeleteModal(skill)} 
                      className="cursor-pointer text-destructive"
                      style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" style={{ flexShrink: 0 }} />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Skill Content */}
              <Link to={`/skills/${skill.id}`} className="block">
                {/* Title */}
                <h3 className="text-xl font-manrope font-bold text-text-dark dark:text-dark-text mb-2 pr-8">
                  {skill.name}
                </h3>
                
                {/* Slug */}
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 font-mono">
                  {skill.slug}
                </p>
                
                {/* Badges: Status + Category (inline, below slug) */}
                <div className="flex items-center gap-2 mb-4">
                  {(() => {
                    let status;
                    if (!skill.is_published || skill.visibility === 'DRAFT') {
                      status = 'DRAFT';
                    } else {
                      status = skill.visibility || 'PUBLIC';
                    }
                    const config = statusConfig[status];
                    return config ? (
                      <Badge className={`flex items-center gap-1 border text-xs ${config.className}`}>
                        {config.icon}
                        <span>{config.label}</span>
                      </Badge>
                    ) : null;
                  })()}
                  
                  {(skill.category || skill.category_id) && (
                    <Badge className="bg-primary text-white border-0 text-xs font-medium">
                      {typeof skill.category === 'object' ? skill.category.name : (skill.category || skill.category_id)}
                    </Badge>
                  )}
                </div>
                
                {/* Description */}
                <p className="text-sm text-text-gray dark:text-dark-text-secondary line-clamp-2 mb-4">
                  {skill.description || 'No description'}
                </p>
                
                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-text-gray dark:text-dark-text-secondary">
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4" />
                    <span>{skill.star_count || 0}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Download className="h-4 w-4" />
                    <span>{skill.install_count || 0}</span>
                  </div>
                </div>
              </Link>
            </GlassCard>
          ))}
        </div>
      )}

      {/* Import Skill Modal */}
      {importModalOpen && (
        <ImportSkillModal
          isOpen={importModalOpen}
          onClose={() => {
            setImportModalOpen(false);
            setImportMethod(null);
          }}
          initialMethod={importMethod}
        />
      )}

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <AlertDialogContent className="glass-strong border-2 border-gray-200 dark:border-[#2e2e2d] bg-white/95 dark:bg-dark-bg-primary/95 backdrop-blur-xl shadow-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-xl font-bold text-text-dark dark:text-dark-text">
              <div className="p-2 rounded-xl bg-destructive/10">
                <Trash2 className="h-6 w-6 text-destructive" />
              </div>
              Delete Skill?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-text-gray dark:text-dark-text-secondary pt-4 text-base leading-relaxed">
              Are you absolutely sure you want to delete <span className="font-bold text-text-dark dark:text-dark-text">"{skillToDelete?.name}"</span>?
              <br /><br />
              <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20">
                <strong className="text-destructive">⚠️ Warning:</strong> This action cannot be undone. All stats and data associated with this skill will be permanently removed.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 pt-6">
            <AlertDialogCancel className="rounded-xl border-2 hover:bg-gray-100 dark:hover:bg-dark-bg-hover">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-xl bg-red-600 text-white hover:bg-red-700 font-semibold shadow-lg border-0 flex items-center justify-center"
              style={{ backgroundColor: '#dc2626', color: 'white', whiteSpace: 'nowrap', minWidth: '140px' }}
            >
              <Trash2 className="h-4 w-4 mr-2" style={{ flexShrink: 0 }} />
              Delete Skill
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Category Modal */}
      <CategoryModal
        isOpen={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          setSkillToEditCategory(null);
        }}
        currentCategory={skillToEditCategory?.category || skillToEditCategory?.category_id}
        onSave={handleSaveCategory}
      />
    </div>
  );
};

export default MySkillsPage;
